// Biletime Supabase Client — Edge Cache Proxy entegreli
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const CF_WORKER_URL = import.meta.env.VITE_CF_WORKER_URL;

// Kişisel/hassas tablolar — proxy'den geçmez, direkt Supabase'e gider
const NO_PROXY_TABLES = [
  'profiles', 'tickets', 'messages', 'payouts', 'reservations',
  'notifications', 'newsletter_subscribers', 'audit_log',
  'moderator_invites', 'payout_bank_accounts',
];

/**
 * Custom fetch: GET /rest/v1/ isteklerini CF Worker proxy'sine yönlendirir.
 * Auth, realtime, storage ve kişisel veri sorguları direkt Supabase'e gider.
 */
const cachedFetch: typeof globalThis.fetch = (input, init) => {
  // Sadece string URL ve GET istekleri için proxy kullan
  if (!CF_WORKER_URL || !input || typeof input !== 'string') {
    return globalThis.fetch(input, init);
  }

  const method = init?.method?.toUpperCase() || 'GET';
  if (method !== 'GET') return globalThis.fetch(input, init);

  // Sadece REST API isteklerini proxy'le
  if (!input.includes('/rest/v1/')) return globalThis.fetch(input, init);

  // Kişisel tabloları proxy'leme
  for (const table of NO_PROXY_TABLES) {
    if (input.includes(`/rest/v1/${table}`)) return globalThis.fetch(input, init);
  }

  // Auth header kontrolü — authenticated user sorguları proxy'leme
  const authHeader = (init?.headers as Record<string, string>)?.['Authorization'] || '';
  if (authHeader && !authHeader.includes(SUPABASE_PUBLISHABLE_KEY)) {
    return globalThis.fetch(input, init);
  }

  // URL'i proxy'ye yönlendir: supabase.co/rest/v1/events → worker/api/rest/v1/events
  try {
    const url = new URL(input);
    const proxyUrl = `${CF_WORKER_URL}/api${url.pathname}${url.search}`;
    return globalThis.fetch(proxyUrl, init)
      .then((res) => {
        if (!res.ok && res.status >= 500) {
          // Fallback to direct Supabase if Cloudflare Worker returns 502/500
          return globalThis.fetch(input, init);
        }
        return res;
      })
      .catch(() => {
        // Proxy network failure
        return globalThis.fetch(input, init);
      });
  } catch {
    return globalThis.fetch(input, init);
  }
};

// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  },
  global: {
    fetch: cachedFetch,
  },
});